import { CheckCircle2, Award, Users } from 'lucide-react';
import { Card } from '../../../shared/components/ui/card';
import { Badge } from '../../../shared/components/ui/badge';
import type { ProviderProfile } from '../types/provider-profile.types';
import { hasValue, hasArrayData } from '../utils/fieldValidation';
import { getProviderDisplayName } from '../utils/formatters';

type ProviderDetailsOverviewProps = {
  provider: ProviderProfile;
  serviceRangesText?: string;
};

export const ProviderDetailsOverview = ({
  provider,
  serviceRangesText,
}: ProviderDetailsOverviewProps) => {
  const companyOverviewParagraphs = (provider.companyOverview || '')
    .split('\n\n')
    .filter(p => p.trim());

  // F059-US2: Include professional title as first paragraph in Bio section
  const providerBioParagraphs = (provider.providerBio || '')
    .split('\n\n')
    .filter(p => p.trim());

  const bioParagraphsWithTitle = hasValue(provider.professionalTitle)
    ? [provider.professionalTitle, ...providerBioParagraphs]
    : providerBioParagraphs;

  // T057: Format professional details for key information
  const displayEstablished = provider.establishedYear?.toString() || '';
  const displayServiceRanges = provider.serviceDeliveryTypes || serviceRangesText || '';
  
  // FR-007e: Display name fallback chain - partnerCompany.name → companyName → (firstName + lastName)
  const displayName = getProviderDisplayName(provider.partnerCompany, provider.companyName, provider.user);

  // Check if we have any content for About section
  const hasAboutContent = hasValue(provider.professionalTitle) || 
                          hasValue(provider.companyOverview) || 
                          hasValue(provider.providerBio);

  // Check if we have any content for Key Information
  const hasKeyInfo = hasValue(displayEstablished) ||
                     hasValue(provider.yearsExperience) ||
                     hasArrayData(provider.registeredStates) ||
                     hasArrayData(provider.professionalLicenses) ||
                     hasValue(displayServiceRanges) ||
                     hasArrayData(provider.languages) ||
                     hasArrayData(provider.professionalAssociations) ||
                     hasArrayData(provider.payerOptions) ||
                     hasArrayData(provider.payerSources) ||
                     hasValue(provider.newClientAcceptance);

  // Check if we have certifications or specialties
  const hasCertOrSpecialties = hasArrayData(provider.certificationsArray) || 
                               hasArrayData(provider.specialties);

  // Only show About card if there's content
  if (!hasAboutContent && !hasCertOrSpecialties && !hasKeyInfo) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* T054: About Section with Company Overview + Bio - Graceful degradation */}
      {hasAboutContent && (
        <Card className="p-6">
          <h2 className="text-xl text-gray-900 mb-4">About {displayName}</h2>

          {(companyOverviewParagraphs.length > 0 || bioParagraphsWithTitle.length > 0) && (
            <div className="space-y-6 mb-4">
              {companyOverviewParagraphs.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900">Company Overview</h3>
                  <div className="space-y-4">
                    {companyOverviewParagraphs.map((paragraph, idx) => (
                      <p key={`company-overview-${idx}`} className="text-gray-700">{paragraph}</p>
                    ))}
                  </div>
                </div>
              )}
              {bioParagraphsWithTitle.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900">Bio</h3>
                  <div className="space-y-4">
                    {bioParagraphsWithTitle.map((paragraph, idx) => (
                      <p key={`provider-bio-${idx}`} className="text-gray-700">{paragraph}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        
          {/* Grid section for certifications and key info - Only show if there's content */}
          {(hasCertOrSpecialties || hasKeyInfo) && (
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* T055: Certifications Section */}
              {hasCertOrSpecialties && (
                <div>
                  {hasArrayData(provider.certificationsArray) ? (
                    <>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Award className="w-5 h-5 text-[#026857]" />
                        Certifications & Credentials
                      </h3>
                      <ul className="space-y-2">
                        {provider.certificationsArray.map((cert, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle2 
                              className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" 
                              data-testid="certification-check"
                            />
                            <span>{cert}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : hasArrayData(provider.specialties) ? (
                    <>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Award className="w-5 h-5 text-[#026857]" />
                        Services & Specialties
                      </h3>
                      <ul className="space-y-2">
                        {provider.specialties.map((specialty) => (
                          <li key={specialty.id} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{specialty.name}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </div>
              )}

              {/* T057: Key Information Section */}
              {hasKeyInfo && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#026857]" />
                    Key Information
                  </h3>
                  <div className="space-y-1 text-sm">
                    {/* Established Year */}
                    {hasValue(displayEstablished) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Established:</span>
                        <span className="text-gray-900 text-right">{displayEstablished}</span>
                      </div>
                    )}

                    {/* T060: Years of Experience */}
                    {hasValue(provider.yearsExperience) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Years of Experience:</span>
                        <span className="text-gray-900 text-right">{provider.yearsExperience}</span>
                      </div>
                    )}

                    {/* T049: Registered States */}
                    {hasArrayData(provider.registeredStates) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">States Licensed In:</span>
                        <span className="text-gray-900 text-right">{provider.registeredStates.join(', ')}</span>
                      </div>
                    )}

                    {/* T048: Professional Licenses */}
                    {hasArrayData(provider.professionalLicenses) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Licenses:</span>
                        <span className="text-gray-900 text-right">{provider.professionalLicenses.join(', ')}</span>
                      </div>
                    )}

                    {/* Service Delivery */}
                    {hasValue(displayServiceRanges) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Service Delivery:</span>
                        <span className="text-gray-900 text-right">{displayServiceRanges}</span>
                      </div>
                    )}

                    {/* T050: Languages */}
                    {hasArrayData(provider.languages) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Languages:</span>
                        <span className="text-gray-900 text-right">{provider.languages.join(', ')}</span>
                      </div>
                    )}

                    {/* T059: Professional Associations */}
                    {hasArrayData(provider.professionalAssociations) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Professional Associations:</span>
                        <span className="text-gray-900 text-right">{provider.professionalAssociations.join(', ')}</span>
                      </div>
                    )}

                    {/* F059-US3: Payment Options */}
                    {(hasArrayData(provider.payerOptions) || hasArrayData(provider.payerSources)) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Payment Options:</span>
                        <span className="text-gray-900 text-right">
                          {[
                            ...(provider.payerOptions?.map(po => po.name) || []),
                            ...(provider.payerSources || [])
                          ].join(', ')}
                        </span>
                      </div>
                    )}

                    {/* New Client Acceptance - Always show this */}
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-600">New Clients:</span>
                      <span className="text-gray-900 text-right">
                        {(provider.newClientAcceptance ?? true) ? 'Accepting' : 'Not Accepting'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* T058: Awards & Recognitions Section - Graceful degradation */}
      {hasArrayData(provider.awardsRecognitions) && (
        <Card className="p-6">
          <h2 className="text-xl text-gray-900 mb-4">Awards & Recognitions</h2>
          <ul className="grid md:grid-cols-2 gap-3">
            {provider.awardsRecognitions.map((award, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
                <CheckCircle2 
                  className="w-4 h-4 text-[#026857] mt-0.5 flex-shrink-0" 
                  data-testid="award-check"
                />
                <span>{award}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Categories & Expertise - Graceful degradation */}
      {(hasArrayData(provider.categories) || hasArrayData(provider.expertise)) && (
        <Card className="p-6">
          {hasArrayData(provider.categories) && (
            <>
              <h2 className="text-xl text-gray-900 mb-4">Categories</h2>
              <div className="flex flex-wrap gap-2 mb-6">
                {provider.categories.map((category) => (
                  <Badge 
                    key={category.id}
                    variant="outline"
                    className="border-[#026857] text-[#026857] px-4 py-2"
                  >
                    {category.name}
                  </Badge>
                ))}
              </div>
            </>
          )}

          {hasArrayData(provider.expertise) && (
            <>
              <h2 className="text-xl text-gray-900 mb-4 mt-6">Areas of Expertise</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {provider.expertise.map((exp) => (
                  <div 
                    key={exp.id}
                    className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg hover:bg-[#026857]/5 transition-colors"
                  >
                    <CheckCircle2 className="w-4 h-4 text-[#026857] flex-shrink-0" />
                    <span>{exp.name}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
};

